// Local scan history: IndexedDB, no wrapper dependency.

import type { ScanRecord } from '../types';

const DB_NAME = 'autospy';
const STORE = 'scans';
const DEDUPE_WINDOW_MS = 60_000;

export interface ScanRecordInput {
  text: string;
  region?: string | undefined;
  confidence?: number | undefined;
  ts?: number | undefined;
  edited?: boolean | undefined;
  favorite?: boolean | undefined;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('ts', 'ts');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb open failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('indexeddb tx failed'));
        t.oncomplete = () => db.close();
      }),
  );
}

export function saveScan(rec: Omit<ScanRecord, 'id'>): Promise<number> {
  return tx('readwrite', (s) => s.add(rec) as IDBRequest<number>);
}

/**
 * Records every route into vehicle details, regardless of whether it came from
 * the camera, manual entry, a gallery image, or an existing history row.
 *
 * Camera locks also write from useScanner so their confidence/favorite state is
 * available immediately. Keeping the de-duplication in one IndexedDB
 * transaction makes those concurrent writes collapse into a single row.
 */
export function recordScan(input: ScanRecordInput): Promise<ScanRecord> {
  const incoming: Omit<ScanRecord, 'id'> = {
    text: input.text,
    region: input.region,
    confidence: input.confidence ?? 0,
    ts: input.ts ?? Date.now(),
    edited: input.edited ?? false,
    favorite: input.favorite ?? false,
  };

  return openDb().then(
    (db) =>
      new Promise<ScanRecord>((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        const cursorRequest = store.index('ts').openCursor(null, 'prev');
        let result: ScanRecord | null = null;

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          const existing = cursor?.value as ScanRecord | undefined;

          if (
            cursor &&
            existing &&
            incoming.ts - existing.ts <= DEDUPE_WINDOW_MS
          ) {
            if (existing.text === incoming.text) {
              result = {
                ...existing,
                region: incoming.region ?? existing.region,
                confidence: input.confidence ?? existing.confidence,
                ts: incoming.ts,
                edited: existing.edited || incoming.edited,
                favorite: existing.favorite || incoming.favorite,
              };
              store.put(result);
              return;
            }
            cursor.continue();
            return;
          }

          const addRequest = store.add(incoming);
          addRequest.onsuccess = () => {
            result = { ...incoming, id: Number(addRequest.result) };
          };
        };

        transaction.oncomplete = () => {
          db.close();
          if (result) resolve(result);
          else reject(new Error('indexeddb history write completed without a record'));
        };
        transaction.onabort = transaction.onerror = () => {
          db.close();
          reject(transaction.error ?? new Error('indexeddb history write failed'));
        };
      }),
  );
}

export function updateScan(rec: ScanRecord): Promise<IDBValidKey> {
  return tx('readwrite', (s) => s.put(rec));
}

export function deleteScan(id: number): Promise<undefined> {
  return tx('readwrite', (s) => s.delete(id) as IDBRequest<undefined>);
}

export async function listScans(): Promise<ScanRecord[]> {
  const all = await tx('readonly', (s) => s.getAll() as IDBRequest<ScanRecord[]>);
  // Records written before the favorite field existed default to false.
  return all.map((r) => ({ ...r, favorite: r.favorite === true })).sort((a, b) => b.ts - a.ts);
}
